import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SquareIcon from "@mui/icons-material/Square";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import SleepDiaryChart, {
  DEFAULT_SLEEP_BAR_ATTR,
} from "../../../commons/SleepDiaryChart";
import { AverageMeasurementData, V2DetailInfo } from "../../commons";
import { SectionTitle, getMeasurementTermString } from "./commons";

export interface InsomniaRiskContent2Props {
  averageMeasurementData: AverageMeasurementData;
  detailInfoArray: V2DetailInfo[];
  measurementPeriodEnd: string;
  measurementPeriodStart: string;
}
export default function InsomniaRiskContent2({
  averageMeasurementData,
  detailInfoArray,
  measurementPeriodEnd,
  measurementPeriodStart,
}: InsomniaRiskContent2Props) {
  const measurementTermString = getMeasurementTermString(
    measurementPeriodStart,
    measurementPeriodEnd
  );

  return (
    <>
      <Stack sx={{ gap: "16px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <SectionTitle text="参考情報：睡眠測定データ" />
          <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
            測定期間：{measurementTermString}
          </Typography>
        </Stack>
        <Stack sx={{ gap: "16px" }}>
          <LegendItems />
          <MeasurementDataChart detailInfoArray={detailInfoArray} />
          <Typography
            sx={{ fontSize: "11.5px", fontWeight: 500, height: "12px" }}
          >
            測定データの平均値
          </Typography>
          <AverageTable data={averageMeasurementData} />
        </Stack>
      </Stack>
    </>
  );
}

function LegendItems() {
  const fontStyle = { fontSize: "10px", fontWeight: 500, alignSelf: "center" };
  const squareStyle = { width: "14px", height: "14px", alignSelf: "center" };
  return (
    <Stack direction="row" gap="22px" sx={{ alignSelf: "end", height: "10px" }}>
      <Stack direction="row" gap="6px">
        <SquareIcon sx={{ ...squareStyle, color: "#01996D80" }} />
        <Typography sx={fontStyle}>覚醒</Typography>
      </Stack>
      <Stack direction="row" gap="6px">
        <SquareIcon sx={{ ...squareStyle, color: "#01996D" }} />
        <Typography sx={fontStyle}>睡眠</Typography>
      </Stack>
      <Stack direction="row" gap="6px">
        <PlayArrowIcon
          sx={{
            rotate: "180deg",
            width: "14px",
            height: "14px",
            alignSelf: "center",
            color: "#FF3A30",
          }}
        />
        <Stack direction="row">
          <Typography sx={fontStyle}>起床希望時刻</Typography>
          <Typography sx={{ ...fontStyle, fontSize: "8px" }}>
            (主観値)
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface MeasurementDataChartProps {
  detailInfoArray: V2DetailInfo[];
}
function MeasurementDataChart({ detailInfoArray }: MeasurementDataChartProps) {
  return (
    <Stack sx={{ height: "618px" }}>
      <SleepDiaryChart
        sleepBarAttributes={{
          ...DEFAULT_SLEEP_BAR_ATTR,
          active: {
            sleep: { fill: "#01996D" },
            awake: { fill: "#80CCB6" },
            no_data: { fill: "#FFFFFF" },
            nap: { fill: "#FFFFFF" },
          },
        }}
        data={detailInfoArray.map((d) => ({
          date: d.date,
          plannedWakeupTime: d.planned_wakeup_time
            ? new Date(d.planned_wakeup_time)
            : null,
          sleepData: d.chart_data.map((c) => ({
            from: new Date(c.start),
            to: new Date(c.end),
            type: c.type,
          })),
        }))}
      />
    </Stack>
  );
}

interface AverageTableProps {
  data: AverageMeasurementData;
}
function AverageTable({ data }: AverageTableProps) {
  const cellStyle = {
    border: "solid #000",
    borderWidth: "0px 1px 1px 0px !important",
    fontSize: "10px",
    fontWeight: "400",
    height: "36px",
    p: 0,
    pl: "8px",
    width: "122px",
  };
  const titleCellStyle = {
    ...cellStyle,
    fontWeight: "500",
    width: "120px",
    backgroundColor: "#EAEAEA80",
  };
  const renderNullable = (value: string | null) => {
    return value == null ? "-" : value;
  };
  return (
    <TableContainer
      sx={{
        border: "solid #000",
        borderWidth: "1px 0px 0px 1px",
        borderRadius: 0,
      }}
    >
      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={titleCellStyle}>消灯時刻</TableCell>
            <TableCell sx={cellStyle}>{renderNullable(data.bedtime)}</TableCell>
            <TableCell sx={titleCellStyle}>入眠時刻</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.sleep_start_time)}
            </TableCell>
            <TableCell sx={titleCellStyle}>覚醒時刻</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.sleep_end_time)}
            </TableCell>
            <TableCell sx={titleCellStyle}>起床時刻</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.arise_time)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={titleCellStyle}>総就床時間</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.total_time_in_bed)}
            </TableCell>
            <TableCell sx={titleCellStyle}>睡眠時間</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.sleep_time)}
            </TableCell>
            <TableCell sx={titleCellStyle}>総睡眠時間</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.total_sleep_time)}
            </TableCell>
            <TableCell sx={titleCellStyle}>睡眠効率</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.sleep_efficiency)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={titleCellStyle}>入眠潜時</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.sleep_latency)}
            </TableCell>
            <TableCell sx={titleCellStyle}>離床潜時</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.wakeup_latency)}
            </TableCell>
            <TableCell sx={titleCellStyle}>中途覚醒時間</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.awakening_time)}
            </TableCell>
            <TableCell sx={titleCellStyle}>覚醒回数</TableCell>
            <TableCell sx={cellStyle}>
              {renderNullable(data.awakening_count)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
